// Libraries
import React from "react";
import { toast } from "react-toastify";
import md5 from "md5";
import _ from "lodash";

// Parent Component/Class
import Form from "./form";

// Child Components/Class
import Viewpoint from "./form/viewpoint";
import Uploader from "./form/uploader";

// Functions
import { getFileType } from "../../utils/scripts/type";

// Constants
const baseUrl = process.env.REACT_APP_API_URL;
const imageSize = process.env.REACT_APP_MAX_IMAGE_SIZE;
const videoSize = process.env.REACT_APP_MAX_VIDEO_SIZE;
const audioSize = process.env.REACT_APP_MAX_AUDIO_SIZE;

class Media extends Form {
  /*-------------------- File Validation --------------------*/
  // File input Validation Function
  validateFileInput = (file) => {
    const fileSize = file.size;
    if (file.type.startsWith("image")) {
      return fileSize > imageSize
        ? `"Image Size" is ${fileSize} bytes but must be less than or equal to ${imageSize} bytes`
        : null;
    } else if (file.type.startsWith("video")) {
      return fileSize > videoSize
        ? `"Video Size" is ${fileSize} bytes but must be less than or equal to ${videoSize} bytes`
        : null;
    } else if (file.type.startsWith("audio")) {
      return fileSize > audioSize
        ? `"Audio Size" is ${fileSize} bytes but must be less than or equal to ${audioSize} bytes`
        : null;
    } else {
      return null;
    }
  };
  /*-------------------- File Validation --------------------*/

  /*-------------------- Functions --------------------*/
  // Fileame Hash Function
  getName = (name) => {
    const index = name.lastIndexOf(".");
    const uniqueName = md5(name);
    const filename = uniqueName + name.slice(index);
    return filename.toLowerCase();
  };

  // Get File mime-type from URL
  getType = async (name, src) => {
    const { types } = this.state;

    if (types[name] === "") {
      const type = await getFileType(src);
      types[name] = type;

      this.setState({ types });
    }
  };

  // Get File source and type for Preview
  getFile = (name, path, folder) => {
    const { data, selectedFiles, selectedImages, inputLabels, types } =
      this.state;

    let type = "";
    let source = "";

    if (selectedFiles[name] && selectedImages.length) {
      source = selectedFiles[name];
      const file = selectedImages.find((i) => i.name === inputLabels[name]);
      type = file.type.split("/")[0];
    } else if (data[folder] && data[name]) {
      source = `${baseUrl}/${path}/${data[folder]}/${data[name]}`;

      this.getType(name, source);

      type = types[name];
    }

    return { source, type };
  };

  // Get Image aspect ratio function
  calculateAspectRatio = ({ target: media }) => {
    const data = { ...this.state.data };

    if (media.id !== "new") {
      const aspectRatio = media.offsetWidth / media.offsetHeight;

      // Aspect Ratio for Audio Files is 1; for other Files will be calculated
      data[media.id] =
        aspectRatio < 4 ? Math.round(aspectRatio * 100) / 100 : 1;
    }

    this.setState({ data });
  };

  // Round Values Function
  roundValue = (number) => {
    return Math.round(number * 100) / 100;
  };
  /*-------------------- Functions --------------------*/

  /*-------------------- Elements --------------------*/
  // File Select Input Event Handler
  handleFileSelect(event) {
    event.preventDefault();

    if (event.target.files.length === 0) return;

    // Add File to Selected Files instead of Browser event
    const selectedFiles = { ...this.state.selectedFiles };
    selectedFiles[event.target.id] = URL.createObjectURL(event.target.files[0]);

    // Check for File Sizes
    const errors = { ...this.state.errors };
    const errorMessage = this.validateFileInput(event.target.files[0]);

    if (errorMessage) {
      errors[event.target.labels[0].innerText] = errorMessage;
    } else {
      delete errors[event.target.labels[0].innerText];
    }

    this.setState({ errors });

    // Get Labels of Selected File
    const data = { ...this.state.data };
    data[event.target.id] = this.getName(event.target.files[0].name);

    this.setState({ data });

    const inputLabels = { ...this.state.inputLabels };
    inputLabels[event.target.id] = event.target.files[0].name;

    if (inputLabels["filename"] && inputLabels["medianame"]) {
      const count = this.state.selectedImages.length;
      const selectedImages = [...this.state.selectedImages];

      if (event.target.id === "filename") {
        if (count === 1) {
          const viewpoint = this.state.selectedImages.shift();
          selectedImages[0] = event.target.files[0];
          selectedImages[1] = viewpoint;
        } else {
          selectedImages[0] = event.target.files[0];
        }
      } else {
        selectedImages[1] = event.target.files[0];
      }

      // Define File Upload Progresses
      const progressInfos = [];

      for (let i = 0; i < selectedImages.length; i++) {
        progressInfos.push({
          percentage: 0,
          fileName: selectedImages[i].name,
          fileType: selectedImages[i].type,
        });
      }

      this.setState({ selectedImages, progresses: progressInfos });
    } else {
      const selectedImages = [...this.state.selectedImages];

      if (selectedImages.length !== 0) {
        selectedImages.shift();
      }
      selectedImages.push(event.target.files[0]);

      // Define File Upload Progresses
      const progressInfos = [];

      for (let i = 0; i < selectedImages.length; i++) {
        progressInfos.push({
          percentage: 0,
          fileName: selectedImages[i].name,
          fileType: selectedImages[i].type,
        });
      }

      this.setState({ selectedImages, progresses: progressInfos });
    }

    this.setState({ selectedFiles, inputLabels });

    // Images
    const images = [];

    for (let i = 0; i < event.target.files.length; i++) {
      images.push(URL.createObjectURL(event.target.files[i]));
    }

    this.setState({ previewImages: images });

    // Labels
    if (event.target.files) {
      const fileNameObject = _.mapValues(event.target.files, function (o) {
        return o.name;
      });

      const labels = Object.keys(fileNameObject).map(function (key) {
        return fileNameObject[key];
      });

      this.setState({ labels });
    }
  }

  // File Uploader + Preview Render Function
  renderMediaUploader = (label, name, path, folder, accept, id = "new") => {
    const { data, inputLabels, errors } = this.state;
    const { source, type } = this.getFile(name, path, folder);

    return (
      <Uploader
        label={label}
        name={name}
        key={name + id}
        source={source}
        type={type}
        inputlabel={inputLabels[name] || data[name]}
        id={id}
        accept={accept}
        value={data[id] || ""}
        error={errors[id]}
        onLoad={this.calculateAspectRatio}
        onChange={this.handleFileSelect}
      />
    );
  };

  // Viewpoint Modal Capture Function
  handleCapture = (imageSrc) => {
    // Capture Preview
    this.setState({ capturedImage: imageSrc });

    // Convert Blob Image to Image Data for Upload
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        // Captured Image Extention
        const extention = blob.type.split("/");

        // Make an Upload File
        let file = new File(
          [blob],
          `${Date.now().toString()}.${extention[1]}`,
          {
            type: blob.type,
          }
        );

        // Image Preview
        const selectedFiles = {};
        selectedFiles["filename"] = imageSrc;

        // Image Label
        const inputLabels = {};
        inputLabels["filename"] = file.name;

        // Image to Upload
        const selectedImages = [];
        selectedImages.push(file);

        // Progress of Upload
        const progressInfos = [];
        progressInfos.push({
          percentage: 0,
          fileName: selectedImages[0].name,
          fileType: selectedImages[0].type,
        });

        // this.setState({
        //   selectedFiles,
        //   inputLabels,
        //   selectedImages,
        //   progresses: progressInfos,
        // });
      })
      .catch((e) => {
        if (e) {
          toast.error(`A Problem occured while capturing!, ${e}`);
        }
      });
  };

  // Viewpoint Modal Retake Function
  handleRetake = (e) => {
    this.setState({ capturedImage: undefined });
  };

  // Save Modal Button Event Handler
  handleModalSave = (e) => {
    const data = { ...this.state.data };

    data["viewpoint_lat"] = e.latitude;
    data["viewpoint_long"] = e.longitude;
    data["viewpoint_alpha"] = this.roundValue(e.alpha);
    data["viewpoint_beta"] = this.roundValue(e.beta);
    data["viewpoint_gamma"] = this.roundValue(e.gamma);

    this.setState({ data });
  };

  // Viewpoint Modal Render Function
  renderModal = (isViewpointForm, name) => {
    const { data, pois, capturedImage } = this.state;
    let imageSource = "";

    if (isViewpointForm) {
      const poi = pois.find((p) => p._id === data.poi_id);
      imageSource = `${baseUrl}/pois/${poi._id}/${poi[name]}`;
    } else {
      imageSource = `${baseUrl}/pois/${data._id}/${data[name]}`;
    }

    return (
      <Viewpoint
        inputImage={imageSource}
        capturedImage={capturedImage}
        capture={this.handleCapture}
        retake={this.handleRetake}
        saveModal={this.handleModalSave}
      />
    );
  };

  // Upload Progress Render Function
  renderUploadStatus = (progresses) => {
    // Check if any File is Uploading
    let hasProgress = false;
    if (progresses.length) {
      hasProgress = progresses[0].percentage > 0 ? true : false;
    }

    return (
      hasProgress &&
      progresses.map((p, index) => (
        <div className="py-1 my-1" key={index}>
          <div
            className="alert alert-secondary my-2"
            role="alert"
            style={{ borderRadius: "15px" }}
          >
            Uploading <span className="font-weight-bold">{p.fileName}</span>.
            Please wait ...
          </div>

          <div className="mb-2">
            <div className="progress rounded">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated progress-bar-info rounded"
                role="progressbar"
                aria-valuenow={p.percentage}
                aria-valuemin="0"
                aria-valuemax="100"
                style={{ width: p.percentage + "%" }}
              >
                {p.percentage}%
              </div>
            </div>
          </div>
        </div>
      ))
    );
  };
  /*-------------------- Elements --------------------*/
}

export default Media;
